import request, { ISettings } from '@common/axios/requestUtil';
import type { VossABResult } from '@/common/typings/abTest';
import { TQAdGroupResponse } from '../typings/voss';
import { resData } from './util';
import { msSessionStorage } from '@common/utils/cache';
import { getVayhApiRequestPath } from '@withdraw/services/utils';
import { useBaseStore } from '@common/stores/storage';
import getAdpPath from '@common/utils/adpPath';
import { getABOnline } from '@common/utils/newABtest';

/** 重置ABCodes */
function resetABCodes(allCodes: string[], newCodes: string[]) {
  const removalSet = new Set(newCodes.map(String)); // 将所有元素转换为字符串并存入 Set
  return allCodes.filter((item) => !removalSet.has(item)); // 检查 item 是否在 removalSet 中
}

export interface BaseResultInterface {
  code: string;
  message: string;
  decryptData: string;
  result: any;
}

export const serviceTime = async () => {
  const response = await request.get(
    '/vayhapi/vayh/app/V1.0/getSystemTimestamp',
    {},
    { hasLoad: false },
  );
  return response?.result || {};
};

/**
 * @function queryVossAbResult
 * @description 对外暴露的分流方法
 * @param {Object} params - 分流参数
 * @param {string} params.moduleCodes - 分流code，多个用","分割
 * @param {number} [params.noSendSensors] - 无需要埋点发送
 * @param {Object} [params.onlineAB] - 默认参数{Key:value}格式
 * @returns {Promise<VossABResult | {}>} - 返回一个 Promise，成功时解析为目标文件的路径
 */
// 做兼容改造 ，不影响以前用法
export async function queryVossAbResult(
  params: {
    moduleCodes: string;
    noSendSensors?: number;
    onlineAB?: {
      [key: string]: string;
    };
  },
  options: ISettings = {
    hasLoad: true,
    errDialog: false,
  },
): Promise<VossABResult | null> {
  let abResponse = {};
  const onlineABCodes = Object.keys(params?.onlineAB || {});

  // new AB
  if (onlineABCodes.length) {
    const onlineResult = await getABOnline({ abkvs: params?.onlineAB });
    onlineABCodes.forEach((item: string) => {
      abResponse = {
        [`${item}`]: {
          value: onlineResult[item] || params?.onlineAB?.[item],
        },
      };
    });
  }

  // voss AB
  let moduleCodes = params.moduleCodes;
  const vossABCodes = moduleCodes.split(',');
  if (onlineABCodes.length) {
    // 如果有onlineAB,则moduleCodes中去除多余ab
    moduleCodes = resetABCodes(vossABCodes, onlineABCodes).join(',');
  }
  const url = '/vayhapi/vayh/ab/V1.0/vossAbResult';
  const result = await request.post(url, { ...params, moduleCodes }, { ...options });
  return Object.assign(result?.result || {}, abResponse);
}

export async function queryOldVossAbResult(
  params: { codes: string },
  hasLoad = false,
): Promise<VossABResult | null> {
  console.log(params);
  const url = '/vayhapi/vayh/ab/V1.0/abResult';
  const result = await request.post(url, params, { hasLoad });
  return result?.result || {};
}

// 查询广告
export const queryAdGroupInfo = async (
  params: {
    pageCode: string;
    adGroupCode: string;
    hasShowMap?: any;
  },
  options: ISettings = {
    hasLoad: true,
    errDialog: false,
  },
): Promise<TQAdGroupResponse | null> => {
  const url = '/vayhapi/vayh/voss/V1.0/adGroupsInfo';
  const result = await request.post(url, params, options);
  return result?.result || {};
  // return {};
};

interface VossAbParams {
  params: string;
}

// 查询ab params
export const queryVossAbParam = async (params: VossAbParams, options = {}) => {
  const url = '/vayhapi/vayh/ab/V1.0/vossAbParam';
  const data = await request.post(url, params, { hasLoad: false, ...options });
  return data?.result || {};
};

// 查询文章
export const queryVossArticlesParam = async (params: any) => {
  const url = '/vayhapi/vayh/voss/V1.0/articles';
  const data = await request.post(url, params, { isUnLoginRequest: true, hasLoad: false });
  return data?.result || {};
};

// 查询配置信息
interface DictParams {
  dictCode: string;
}
export const queryDict = async (params: DictParams) => {
  const url = '/vayhapi/vayh/router/V1.0/queryDict';
  const data = await request.post(url, params, { hasLoad: false });
  return data?.result || {};
};

export interface GetImgCodeInnerInterface {
  picId: string;
  base64Img: string;
}

export interface GetImgCodeResultInterface extends IResponse {
  result: GetImgCodeInnerInterface;
}

interface QueryVcode {
  tokenType: string;
  url?: string;
}

interface VCodeResponse {
  codeType: string;
  nextUrl: string | null;
  vcode: string;
}
// 生成
export const getVcode = async (params: QueryVcode): Promise<VCodeResponse> => {
  const url = '/vayhapi/vayh/auth/V1.0/getVcode';
  const response = await request.post(url, params);
  return response?.result || {};
};

// 获取图片验证码
export const getImgCode = async (): Promise<GetImgCodeResultInterface> => {
  const result = await request.post(`/vayh/sms/V1.0/imgcode/base64`, {});
  return result as GetImgCodeResultInterface;
};

// 获取营销token
export const getMarketToken = async (params) => {
  const data = await request.post('/vayhapi/vayh/my/V1.0/getMarketToken', params, {
    hasLoad: false,
  });
  if (data?.result?.onceToken) {
    return data.result.onceToken;
  }
  const fail_reason = data?.message || '获取营销会员token失败';
  window.REPORT_TRACK('web_error_log', {
    fail_reason,
    ...(data?.result || {}),
  });
  return null;
};

// 存储token
export const saveVtoken = async (params) => {
  const url = location.origin + '/common/saveVtoken';
  const result = await request.get(url, params, {
    hasLoad: true,
    errDialog: false,
    ignoreError: true,
  });
  return resData(result);
};

export interface SendMessageParamsInterface {
  identityCard: IdentityCardInterface;
  bankCard: BankCardInterface;
  oldBankCardId?: string;
  scene?: string;
  fromWhere?: string;
}

interface IdentityCardInterface {
  name?: string;
  number?: string;
  aesName?: string;
  aesIdent?: string;
}

interface BankCardInterface {
  provinceCode: string;
  cityCode: string;
  provinceName: string;
  cityName: string;
  mobile?: string;
  number?: string;
  aesMobile?: string;
  aesBank?: string;
}

interface BindResult {
  templateProtocolList: Array<any>;
  authType: string;
  bankcardCaptchaId: string;
  isStudentFlag: boolean;
}

export interface BindCardResultInterface extends IResponse {
  result: BindResult;
}

export const sendMessage = async (
  params: SendMessageParamsInterface,
): Promise<BindCardResultInterface> => {
  // todo: normalPost -> post, 需要测试 by allen
  const response = await request.post('/vayh/sms/V1.0/binding', params, {
    ignoreErrorCode: true,
  });
  return response as BindCardResultInterface;
};

interface showPaymentProtocolParams {
  bankcardCaptchaId?: string;
  bankcardCaptcha?: string;
  isMaster: number;
  templateCode?: string;
  templateVersion?: string;
}

export const getPaymentProtocol = async (params: showPaymentProtocolParams): Promise<IResponse> => {
  const result = await request.post('/vayh/protocol/V1.0/bankCardProtocol', params);
  return result?.data || {};
};

interface getAyhCommonProtocalParams {
  name?: string;
  bankCardNo?: string;
  signSelectionFlag?: boolean;
  signValidity?: string;
  templateCode?: string;
  templateVersion?: string;
}

/**通用协议 */
export const getAyhCommonProtocal = async (
  params: getAyhCommonProtocalParams,
): Promise<IResponse> => {
  const url = '/vayh/protocol/V1.0/getCommonProtocal';

  const result = await request.post(url, params);

  return result?.data || '';
};

export const alipayAuthNew = async () => {
  const url = '/vayh/alipay/V1.0/authNew';

  const result = await request.get(url);

  return result?.result;
};

export interface userInfoInnerInterface {
  mobile: string;
  /** 姓名 */
  name: string;
  /** 身份证号 */
  ident: string;
  /** 身份证是否过期 */
  isIdcardExpired: any;
  userId: number;
  /** uniqueI */
  uniqueId: string;
}

export interface userInfoResultInterface extends IResponse {
  result: userInfoInnerInterface;
}

// 获取用户基本信息
export const profileforhidden = async (): Promise<userInfoInnerInterface> => {
  const url = getVayhApiRequestPath('vayh/my/V1.0/profileforhidden');
  const result = await request.post(url, {}, { hasLoad: false });
  return (result?.result as userInfoInnerInterface) || {};
};

interface ILoginParams {
  vcode: string;
}

interface ILoginResultInner {
  appId: string;
  appKey: string;
  bindingId: string;
  dlChannel: string;
  productCode: string;
  userId: string;
  vtoken: string;
}

interface InterfaceILoginResult extends IResponse {
  result: ILoginResultInner;
}

/**
 * 联合登录接口
 */
export const loginer = async (params: ILoginParams): Promise<IResponse | null> => {
  const url = '/vayh/inner/V1.0/h5login';
  const result = await request.post(url, params);

  return result as InterfaceILoginResult;
};

interface AppConfig {
  /**用户id，临时的 */
  userId: number;
  /**appKey */
  appKey?: string;
  /**system */
  system?: string;
}

/**
 * 应用配置
 */
export const queryAppConfig = async (): Promise<AppConfig> => {
  const url = '/json/app.json';
  const response = await request.get(url, {}, { ignoreErrorCode: true });
  return (response || {}) as AppConfig;
};

/**
 * 应用配置
 */
export const queryLoanConfig = async (namespace: string): Promise<any> => {
  if (!namespace) {
    return {
      title: '',
      subTitle: '',
    };
  }
  const url = `/json/${namespace}.json`;
  const response = await request.get(url);
  return response?.result || {};
};

interface ILoanHasBankSignResult {
  hasBankSign: boolean;
}

/**
 * 查询是否有签约银行卡
 */
export const loanHasBankSign = async (): Promise<ILoanHasBankSignResult> => {
  const url = `/vayh/cashflow/V1.0/hasBankSig`;

  const result = await request.get(url);

  return result?.result as ILoanHasBankSignResult;
};

interface INoWaitFlowResult {
  ocrFailCount: number;
  bankCardFailCount: number;
}

/**
 * 获取ocr或者银行卡鉴权失败次数
 */
export const noWaitFlow = async (): Promise<INoWaitFlowResult> => {
  const url = `/vayh/cashflow/V1.0/noWaitFlow`;

  const result = await request.get(url);

  return result?.result as INoWaitFlowResult;
};

interface IGetApplyResultParams {
  applyNo: string;
  creditConfirm: string;
}

interface IGetApplyResult {
  status: string;
  applyStatus: string;
}

/**
 * 获取申请结果
 */
export const getApplyResult = async (params: IGetApplyResultParams): Promise<IGetApplyResult> => {
  const url = `/vayh/staff/V1.0/applyResult`;
  const result = await request.post(url, params);

  return result?.result as IGetApplyResult;
};

interface IAesBankCardNumberOCRParams {
  bankCardBase64: string;
}

// interface IAesBankCardNumberOCRResult {
//   aesBank:
// }

export const aesBankCardNumberOCR = async (params: IAesBankCardNumberOCRParams) => {
  const url = `/vayh/bank/V1.0/aes/bankCardNumberOCR`;
  const result = await request.post(url, params);

  return result;
};

export async function fetchChannelMsg(): Promise<any> {
  const url = '/vayhapi/vayh/dl/V1.0/channelMsg';
  const result = await request.get(url, {}, { hasLoad: false, ignoreError: true });
  return result?.result || {};
}

/**
 * 查询广告（兼容查询天权1.0和ADP）
 * @param params
 * @returns
 */
export async function queryAdGroupsInfoV2(
  params: {
    pageCode?: string;
    adGroupCode?: string;
    slotCode?: string;
    [key: string]: any;
    isFunctionalPosition?: boolean;
  },
  options?: ISettings,
): Promise<any> {
  const { apiKey = 'adGroupsInfo', ...payLoad } = options ?? {};
  const requestOptions = Object.assign(
    {
      hasLoad: true,
      errDialog: false,
    },
    payLoad,
  );
  try {
    // 包含slotCode，即调用adp
    if (params?.slotCode) {
      const { appKey, productCode, userId, cooperatorId, appName } = useBaseStore();
      let adSlots: any[] = [];
      if (params.pageCode && params.adGroupCode) {
        adSlots.push({
          oldUri: `/vayh/voss/V1.0/${apiKey}`,
          ...params,
        });
      } else {
        // 全新adp广告场景，可支持传入多个slotCode
        const slotCodes: string[] = params.slotCode.split(',');
        const { slotCode, pageCode, adGroupCode, ...otherParams } = params;
        adSlots = slotCodes.map((slotCode) => {
          return {
            slotCode,
            ...otherParams,
            contractSource: appName,
            cashChannelCode: cooperatorId,
          };
        });
      }

      // TODO: adp广告请求域名独立使用eapi
      const adeUrl = getAdpPath();

      // 重新封装入参
      const syh_code_arr: string[] = [];
      adSlots.forEach((item) => {
        syh_code_arr.push(item.slotCode);
      });
      window.REPORT_TRACK('syh_process_info', {
        syh_type: 'adp',
        syh_code: syh_code_arr.join(),
        syh_desc: 'fetch',
      });
      const data = await request.post(
        // 是否功能位
        `${adeUrl}/ms/${params.isFunctionalPosition ? 'adefunction' : 'ad'}/query`,
        {
          timeStamps: Date.now(),
          userId,
          appKey,
          productCode,
          adSlots: adSlots,
        },
        requestOptions,
      );

      const result = resData(data);
      syh_code_arr.forEach((item) => {
        window.REPORT_TRACK('syh_process_info', {
          syh_type: 'adp',
          syh_code: syh_code_arr.join(),
          syh_desc: 'response',
          is_success: result?.ads?.[item] ? 1 : 0,
        });
      });
      return result;
    }
    // 是否存在pageCode和adGroupCode，查询老天权广告
    // if (params.pageCode && params.adGroupCode) {
    //   return resData(
    //     await request.post(`/vayhapi/vayh/voss/V1.0/${apiKey}`, params, requestOptions),
    //   );
    // }
    return null;
  } catch {
    return null;
  }
}

/**
 * 获取支付宝appId等相关数据
 */
export async function getAlipayInfo(params = {}, options?: ISettings): Promise<any> {
  const url = '/vayhapi/vayh/alipay/V1.0/alipayInfo';
  const result = await request.post(url, params, options);
  return resData(result);
}

/**
 * 获取支付宝订阅消息数据
 */
export async function querySubscribeRealtion(params = {}, options?: ISettings): Promise<any> {
  const url = '/vayhapi/vayh/xcx/V1.0/querySubscribeRealtion';
  const result = await request.post(url, params, options);
  return resData(result);
}

export async function queryNodeTime(params = {}, options?: ISettings): Promise<any> {
  const url = '/common/nodeTime';
  const result = await request.get(url, params, options);
  return resData(result);
}

/**
 * 获取活动中心token
 */
export async function getCouponToken(
  params = {},
  options?: ISettings,
): Promise<{ token: string } | undefined> {
  const url = '/vayhapi/vayh/activity/V1.0/nasgateway/token';
  const result = await request.post(url, params, options);
  return resData(result) as { token: string } | undefined;
}

// 获取主体客服电话
export const queryServeceNumber = async (params) => {
  const url = '/vayhapi/vayh/hotline/V1.0/getServiceHotline';
  const data = await request.post(url, params);
  return data;
};

// 查询tcs的位置信息
export const getTCSMarketPosition = async (params = {}, options?: ISettings) => {
  const url = '/cap/config/content/V1/query';
  const { appKey, productCode, userId } = useBaseStore();
  params = {
    appKey,
    userId,
    productCode,
    ...params,
  };
  const result = await request.post(url, params, { hasLoad: false, errDialog: false, ...options });
  return resData(result);
};

/**
 *
 * @param {*} params
    position 位置  1-首页，2-提现uas失败页，3-贷超列表页（金刚区），4-授信失败页 5-CLM挡板 6-提现准入拒绝
    applyNo  授信单号
    cashNo   取现单号
 * @returns
 */
export async function fetchRecommendList(params = {}, options?: ISettings) {
  const url = '/vayhapi/vayh/super/V1.0/recommend/list';
  const result = await request.post(url, params, { hasLoad: false, errDialog: false, ...options });
  return resData(result);
}

/**
 * APP事件上报
 */
export async function queryEventReport(
  params: {
    eventType: string;
    userBehavior?: {
      refNbr?: string;
      contractNo?: string;
    };
  },
  options?: ISettings,
): Promise<any> {
  const url = '/vayhapi/vayh/message/V1.0/appEventReport';
  const result = await request.post(url, params, { hasLoad: false });
  return resData(result);
}

// 查询新天权ab分流（服务端）
export const queryAbResult2 = async (params) => {
  const url = '/vayhapi/vayh/ab/V2.0/abResult';
  const data = await request.post(url, params, { hasLoad: false });
  return data?.result || {};
};
/**
 * 自动还款服务 签约
 */
export const bankSignInfo = async (params = {}) => {
  const response: IResponse<any> | null = await request.post(
    '/pub/infoAuth/bankSignInfo/V1/query',
    params,
  );
  return resData(response);
};
/**
 * 银行卡签约发送短信验证码
 */
export const bankCardAuthSend = async (params = {}) => {
  const response: IResponse<any> | null = await request.post(
    '/pub/infoAuth/bankCardAuthSms/V1/send',
    params,
  );
  return resData(response);
};
/**
 * 银行卡签约短信验证码确认
 */
export const bankCardAuthConfirm = async (params = {}) => {
  const response: IResponse<any> | null = await request.post(
    '/pub/infoAuth/bankCardAuthSms/V1/confrim',
    params,
  );
  return response;
};
// vcode免登
export const loginByVcode = async (params = {}) => {
  const result = await request.post('/oauth/login', params, {
    ignoreError: true,
  });
  return resData(result);
};

/**
 * 收还款确认书保存功能
 */
export const getbindingAndGenProtocol = async (params: any): Promise<IResponse | null> => {
  const url = '/vayhapi/vayh/bank/V1.0/bindingAndGenProtocol';
  const result = await request.post(url, params);
  return result;
};

export const aesProfileforhidden = async (data = {}, options: any): Promise<any> => {
  const url = '/vayhapi/vayh/my/V1.0/aes/profileforhidden';
  const result = await request.post(url, data);
  return result?.result || {};
};

export const getbinding = async (params: any, options: any): Promise<any> => {
  const response = await request.post('/vayhapi/vayh/sms/V1.0/binding', params, {
    ignoreErrorCode: true,
    ...options,
  });
  return response;
};

export const getAesBankCardNumberOCR = async (
  params: IAesBankCardNumberOCRParams,
  options: any,
) => {
  const url = `/vayhapi/vayh/bank/V1.0/aes/bankCardNumberOCR`;
  const result = await request.post(url, params, options);
  return result;
};

export const queryLoanBanks = async (params = { scene: 'loan' }): Promise<any> => {
  const url = '/vayhapi/vayh/bank/V1.0/banks';
  const result = await request.post(url, params);
  return result?.result;
};

export const submitContactsNew = async (params: any, options): Promise<any> => {
  const url = '/vayhapi/vayh/cashflow/V1.0/bindContactsOptimize';
  const result = await request.post(url, params, options);
  return result;
};

export const queryUserCheckGuide = async (params = {}) => {
  const url = '/vayhapi/vayh/cashflow/V1.0/checkGuide';
  const result = await request.post(url, params);
  return result?.result;
};

export const getCommonProtocal = async (params: getAyhCommonProtocalParams): Promise<any> => {
  const url = '/vayhapi/vayh/protocol/V1.0/getCommonProtocal';

  const result = await request.post(url, params);
  return result?.result || '';
};

export const fetchLiveActivityId = async (params = {}): Promise<any> => {
  // const url = '/vayhapi/vayh/my/V1.0/getLiveActivityId';
  const url = '/cap/marketing/liveActivity/V1/query';
  const result = await request.post(url, params);
  return result?.result || {};
};

export async function queryVossAbResultV2(
  params: { moduleCodes: string },
  hasLoad = false,
): Promise<VossABResult> {
  const url = '/vayhapi/vayh/ab/V1.0/vossAbResult';
  const result = await request.post(url, params, { hasLoad });
  return result?.result || {};
}
/**
 * 获取tcs配置内容
 * @param params
 * @param options
 * @returns
 */
export const queryTCSConfig = async (params = {}, options?: ISettings) => {
  const url = '/cap/config/content/V1/query';
  const { appKey, productCode, userId } = useBaseStore();
  params = {
    appKey,
    userId,
    productCode,
    ...params,
  };
  const result = await request.post(url, params, { hasLoad: false, errDialog: false, ...options });
  return resData(result);
};

/**
 * 天权楼层接口
 * @param params
 * @param options
 * @returns
 */
export const getCapFloorData = async (params = {}, options?: ISettings) => {
  const url = '/cap/floor/adsQuery';
  const { appKey, userId } = useBaseStore();
  const data = await request.post(
    url,
    {
      timeStamps: Date.now(),
      userId,
      appKey,
      ...params,
    },
    { hasLoad: false, errDialog: false, ...options },
  );
  return resData(data);
};
